import PersonIcon from '@mui/icons-material/Person';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { Grid, Stack } from '@mui/material';
import React from 'react';
import { useGetGuestUserProfileQuery } from '../../../app/Slices/SevenDayApiSlice';
import ProfileReadOnlyField, {
    ProfileStatusIndicator,
} from '../Profile/ProfileReadOnlyField';
import ProfileSectionCard from '../Profile/ProfileSectionCard';
import GuestPublicProfileRow from '../Reviews/GuestPublicProfileRow';
import { AdminRole, ContractorRole, OwnerRole } from '../../enums/users';
import { capitalizeName } from '../../../shared/utils';
import ManagedUserLoginSecurityPanel, {
    type ManagedUserInvite,
} from './ManagedUserLoginSecurityPanel';

type UserRow = {
    id?: number;
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
    role?: string;
    viewOnly?: boolean;
    isActive?: boolean;
    vehiclePlate?: string | null;
    lastLoginAt?: string | Date | null;
    department?: { name?: string } | null;
};

function accessLabel(user: UserRow | undefined, viewOnly: boolean | undefined) {
    if (!user?.role) return '—';
    if (
        [AdminRole.Admin, OwnerRole.Admin, ContractorRole.Admin].includes(
            user.role as AdminRole,
        )
    )
        return 'Admin';
    if (user.role === 'guest_admin') return 'Company Admin';
    if (user.role === 'guest_department_admin') return 'Team Admin';
    if (viewOnly) return 'View Only User';
    return 'User';
}

export default function UserViewReadOnlyPanels({
    user,
    loggedInRole,
    viewOnly,
    onBeginEditPersonal,
    onBeginEditLogin,
    canEdit,
    onSendInvite,
    canSendInvite,
    invite,
}: {
    user: UserRow | undefined;
    loggedInRole?: string;
    viewOnly?: boolean;
    onBeginEditPersonal: () => void;
    onBeginEditLogin: () => void;
    canEdit: boolean;
    onSendInvite: () => void;
    canSendInvite: boolean;
    invite?: ManagedUserInvite | null;
}) {
    const teamName = user?.department?.name
        ? capitalizeName(user.department.name)
        : '—';
    const isGuestViewer = Boolean(loggedInRole?.includes('guest'));
    const { data: guestUserProfile } = useGetGuestUserProfileQuery(
        user?.id ?? 0,
        {
            skip: !isGuestViewer || !user?.id,
        },
    );

    return (
        <Stack spacing={3} sx={{ width: '100%' }}>
            <ProfileSectionCard
                icon={<PersonIcon sx={{ fontSize: 22 }} />}
                title='Personal details'
                subtitle='Personal information, role, and account access.'
                editButtonLabel='Edit personal details'
                onEdit={onBeginEditPersonal}
                showEditButton={canEdit}
            >
                {isGuestViewer && user?.id ? (
                    <GuestPublicProfileRow
                        averageRating={guestUserProfile?.averageRating}
                        reviewCount={guestUserProfile?.reviewCount}
                        to={`/guest-users/${user.id}`}
                    />
                ) : null}
                <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ rowGap: 0.5 }}>
                    <Grid item xs={12} sm={6} md={4}>
                        <ProfileReadOnlyField label='First name'>
                            {user?.firstName}
                        </ProfileReadOnlyField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <ProfileReadOnlyField label='Surname'>
                            {user?.lastName}
                        </ProfileReadOnlyField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <ProfileReadOnlyField label='Phone'>
                            {user?.phone}
                        </ProfileReadOnlyField>
                    </Grid>
                    {loggedInRole?.includes('guest') ? (
                        <Grid item xs={12} sm={6} md={4}>
                            <ProfileReadOnlyField label='Team'>
                                {teamName}
                            </ProfileReadOnlyField>
                        </Grid>
                    ) : null}
                    <Grid item xs={12} sm={6} md={4}>
                        <ProfileReadOnlyField label='Access'>
                            {accessLabel(user, viewOnly)}
                        </ProfileReadOnlyField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <ProfileReadOnlyField label='Status'>
                            <ProfileStatusIndicator active={user?.isActive} />
                        </ProfileReadOnlyField>
                    </Grid>
                    {loggedInRole !== AdminRole.Admin ? (
                        <Grid item xs={12} sm={6} md={4}>
                            <ProfileReadOnlyField label='Vehicle plate'>
                                {user?.vehiclePlate}
                            </ProfileReadOnlyField>
                        </Grid>
                    ) : null}
                </Grid>
            </ProfileSectionCard>

            <ProfileSectionCard
                icon={<ShieldOutlinedIcon sx={{ fontSize: 12 }} />}
                title='Login & security'
                subtitle='Login email and 7DAY Invite Access'
                editButtonLabel='Edit login & security'
                onEdit={onBeginEditLogin}
                showEditButton={canEdit}
            >
                <ManagedUserLoginSecurityPanel
                    email={user?.email}
                    lastLoginAt={user?.lastLoginAt}
                    invite={invite}
                    canSendInvite={canSendInvite}
                    onSendOrResendInvite={onSendInvite}
                    sendInviteDisabled={user?.isActive === false}
                />
            </ProfileSectionCard>
        </Stack>
    );
}
